import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import { 
  generateMapsUrl, 
  generateGoogleMapsUrl, 
  calculateDistance,
  getTileLayerUrl,
  getTileLayerAttribution
} from '../utils/locationUtils';
import '../styles/LocationMessage.css';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function LocationMessage({ 
  locationData, 
  isOwn = false, 
  timestamp, 
  senderName = '',
  currentUserLocation = null 
}) {
  const [showFullMap, setShowFullMap] = useState(false);

  if (!locationData || !locationData.latitude || !locationData.longitude) {
    return (
      <div className={`location-message error ${isOwn ? 'own' : ''}`}>
        <p>Invalid location data</p>
      </div>
    );
  }

  const { latitude, longitude, address } = locationData;
  const distance = currentUserLocation 
    ? calculateDistance(
        currentUserLocation.latitude, 
        currentUserLocation.longitude, 
        latitude, 
        longitude
      )
    : null;

  const handleOpenInMaps = () => {
    // Try OpenStreetMap first, fallback to Google Maps
    const osmUrl = generateMapsUrl(latitude, longitude);
    const googleUrl = generateGoogleMapsUrl(latitude, longitude);
    
    // For better user experience, provide both options
    const userChoice = window.confirm(
      'Choose map service:\nOK = OpenStreetMap\nCancel = Google Maps'
    );
    
    if (userChoice) {
      window.open(osmUrl, '_blank');
    } else {
      window.open(googleUrl, '_blank');
    }
  };

  const handleToggleFullMap = () => {
    setShowFullMap(!showFullMap);
  };

  return (
    <div className={`location-message ${isOwn ? 'own' : ''}`}>
      <div className="location-message-header">
        <div className="location-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
        </div>
        <span className="location-title">
          {isOwn ? 'Your Location' : `${senderName}'s Location`}
        </span>
        {distance && (
          <span className="location-distance">
            {distance < 1 ? `${Math.round(distance * 1000)}m` : `${distance}km`} away
          </span>
        )}
      </div>

      <div className="location-map-container">
        <MapContainer
          center={[latitude, longitude]}
          zoom={showFullMap ? 16 : 14}
          style={{ 
            width: '100%', 
            height: showFullMap ? '300px' : '150px',
            borderRadius: '8px'
          }}
          scrollWheelZoom={false}
          dragging={showFullMap}
          zoomControl={showFullMap}
          doubleClickZoom={showFullMap}
          touchZoom={showFullMap}
        >
          <TileLayer
            url={getTileLayerUrl()}
            attribution={getTileLayerAttribution()}
          />
          <Marker position={[latitude, longitude]} />
        </MapContainer>
      </div>

      <div className="location-info">
        <div className="location-address">
          <span>{address}</span>
        </div>
        <div className="location-coordinates">
          <small>{latitude.toFixed(6)}, {longitude.toFixed(6)}</small>
        </div>
      </div>

      <div className="location-actions">
        {/* <button 
          className="toggle-map-btn"
          onClick={handleToggleFullMap}
        >
          {showFullMap ? 'Show Less' : 'Show More'}
        </button> */}
        <button 
          className="open-maps-btn"
          onClick={handleOpenInMaps}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
            <polyline points="15,3 21,3 21,9"/>
            <line x1="10" y1="14" x2="21" y2="3"/>
          </svg>
          Open in Maps
        </button>
      </div>

      {timestamp && (
        <div className="location-timestamp">
          <small>{new Date(timestamp).toLocaleTimeString()}</small>
        </div>
      )}
    </div>
  );
}

export default LocationMessage;
